"use client";

import { useEffect, useRef, useState } from "react";

export default function PageBackground({ children }: { children: React.ReactNode }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = () => {
      video.play().catch(() => {
        // Autoplay blocked — try on first interaction
        const handleInteraction = () => {
          video.play().catch(() => {});
          document.removeEventListener("click", handleInteraction);
          document.removeEventListener("touchstart", handleInteraction);
        };
        document.addEventListener("click", handleInteraction);
        document.addEventListener("touchstart", handleInteraction);
      });
    };

    // Only try to play once the video has enough data
    if (video.readyState >= 3) {
      setVideoLoaded(true);
      tryPlay();
    }

    video.addEventListener("canplay", () => {
      setVideoLoaded(true);
      tryPlay();
    }, { once: true });

    return () => {
      video.removeEventListener("canplay", () => {});
    };
  }, []);

  return (
    <div className="relative min-h-screen">
      {/* Static background image (fast loading) */}
      <div
        className="fixed inset-0 z-0 h-full w-full bg-cover bg-center"
        style={{
          backgroundImage: "url('/images/about.png')",
          filter: "brightness(0.25) contrast(1.1)",
          backgroundColor: "#0A0A0A",
        }}
      />

      {/* Background video - lazy loaded, only visible once ready */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="none"
        className="fixed inset-0 z-0 h-full w-full object-cover transition-opacity duration-1000"
        style={{
          filter: "brightness(0.4) contrast(1.1)",
          opacity: videoLoaded ? 1 : 0,
        }}
      >
        <source src="/videos/page-bg.mp4" type="video/mp4" />
      </video>

      {/* Subtle overlay */}
      <div className="fixed inset-0 z-[1] bg-black/25" />

      {/* Page content above video */}
      <div className="relative z-[2]">
        {children}
      </div>
    </div>
  );
}
